import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import Cookies from 'js-cookie';
import styled from 'styled-components';
import { getParticipant } from './participantService';
import { Address, ParticipantResponse } from '../../types';
import { useGlobalContext } from '../../store/GlobalContext';
import DetailPage, {
  DetailFields,
  DetailRelated,
  DetailSection,
  DetailStats,
  Field,
  formatAddress,
} from '../../components/DetailPage';
import { formatEmail } from '../estatelist/estatedetails';

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
`;

const AddressCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const AddressBody = styled.div`
  font-size: 0.9rem;
  color: #333;
  white-space: pre-line;
`;

const AddressPhones = styled.div`
  font-size: 0.75rem;
  color: #888888;
`;

const fullName = (person?: Partial<ParticipantResponse> | null) =>
  [person?.prefix, person?.firstName, person?.middleName, person?.lastName, person?.suffix]
    .filter(Boolean)
    .join(' ');

/** A relationship as the participant endpoint returns it, both sides included. */
interface ParticipantRelationship {
  uid: string;
  relationship: string;
  user1?: Partial<ParticipantResponse>;
  user2?: Partial<ParticipantResponse>;
}

interface ParticipantDetail extends Omit<ParticipantResponse, 'relationships'> {
  relationships?: ParticipantRelationship[];
  sensitiveFieldsNotice?: string;
}

const ParticipantDetails: React.FC = () => {
  const { participantId } = useParams<{ participantId: string }>();
  const navigate = useNavigate();
  const { selectedEstate, auth } = useGlobalContext();
  const [participant, setParticipant] = useState<ParticipantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!selectedEstate?.uuid || !participantId) {
        setError('No active estate is selected.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = auth.accessToken || Cookies.get('accessToken') || '';
        setParticipant(await getParticipant(selectedEstate.uuid, participantId, token));
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load participant');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [participantId, selectedEstate?.uuid, auth.accessToken]);

  const name = fullName(participant);
  const addresses = (participant?.addresses || []) as Address[];

  /*
   * A relationship names two people, and which one is "the other" depends on
   * which side this participant sits. The stored sentence reads "user2 is a
   * <relationship> to user1", so the label flips with the side.
   */
  const relationships = (participant?.relationships || []).map((rel) => {
    const isUser1 = rel.user1?.uid === participant?.uid;
    const other = isUser1 ? rel.user2 : rel.user1;
    const description = isUser1
      ? `${fullName(other) || 'Someone'} is their ${rel.relationship}`
      : `They are the ${rel.relationship} of ${fullName(other) || 'someone'}`;

    return {
      key: rel.uid,
      name: fullName(other) || 'Unnamed person',
      meta: description,
      onClick: other?.uid ? () => navigate(`/participants/${other.uid}`) : undefined,
    };
  });

  const estates = participant?.estates || [];

  const tags = [
    participant?.deceased ? { label: 'Deceased', color: 'default' } : { label: 'Living', color: 'green' },
    participant?.userType ? { label: participant.userType, color: 'blue' } : null,
  ].filter(Boolean) as { label: string; color?: string }[];

  return (
    <DetailPage
      eyebrow="Participant"
      title={name || formatEmail(participant?.email, 'Participant')}
      icon={<UserOutlined />}
      tags={tags}
      loading={loading}
      error={error}
      backTo="/participants"
      backLabel="Back to participants"
    >
      {/* Deliberately no email here: it has no spaces to wrap on, so a long one
          breaks mid-word at this size. It reads fine in the fields below. */}
      <DetailStats
        stats={[
          {
            label: 'Date of birth',
            value: participant?.dob ? moment(participant.dob).format('LL') : null,
          },
          { label: 'Phone', value: participant?.phone },
          { label: 'Addresses', value: addresses.length ? `${addresses.length}` : null, emptyText: 'None' },
          { label: 'Relationships', value: relationships.length ? `${relationships.length}` : null, emptyText: 'None' },
        ]}
      />

      <DetailSection title="Personal details">
        <DetailFields>
          <Field label="Full name" value={name} span />
          <Field label="Email" value={participant ? formatEmail(participant.email) : null} />
          <Field label="Phone" value={participant?.phone} />
          <Field label="Sex" value={participant?.sex} />
          <Field label="Marital status" value={participant?.maritalStatus} />
          <Field
            label="Date of birth"
            value={participant?.dob ? moment(participant.dob).format('LL') : null}
          />
          <Field
            label="Date of death"
            value={participant?.dateOfDeath ? moment(participant.dateOfDeath).format('LL') : null}
            emptyText={participant?.deceased ? 'Not recorded' : 'Not applicable'}
          />
          <Field label="SSN" value={participant?.ssn} />
          <Field label="Representative" value={participant?.representative} />
          <Field label="Relationship to estate" value={participant?.natureOfRelation} />
        </DetailFields>
      </DetailSection>

      <DetailSection
        title={`Addresses (${addresses.length})`}
        empty={!addresses.length}
        emptyText="No address has been recorded for this person"
      >
        <AddressGrid>
          {addresses.map((address, index) => {
            const phones = [address.primaryPhone, address.alternatePhone1, address.alternatePhone2]
              .filter(Boolean)
              .join(' / ');

            return (
              <AddressCard key={address.addressLine1 ? `${address.addressLine1}-${index}` : index}>
                <Tag color="blue" style={{ alignSelf: 'flex-start' }}>
                  {address.addressType || 'Address'}
                </Tag>
                <AddressBody>{formatAddress(address) || 'Empty address record'}</AddressBody>
                {phones && <AddressPhones>{phones}</AddressPhones>}
              </AddressCard>
            );
          })}
        </AddressGrid>
      </DetailSection>

      <DetailSection
        title={`Relationships (${relationships.length})`}
        empty={!relationships.length}
        emptyText="No family relationships have been recorded"
      >
        <DetailRelated items={relationships} />
      </DetailSection>

      <DetailSection
        title={`Estates (${estates.length})`}
        empty={!estates.length}
        emptyText="Not attached to any estate"
      >
        <DetailRelated
          items={estates.map((estate) => ({
            key: estate.uuid,
            name: estate.estateName,
            meta: estate.description,
            onClick: () => navigate(`/estate/${estate.uuid}`),
          }))}
        />
      </DetailSection>

      {participant?.sensitiveFieldsNotice && (
        <Alert type="info" showIcon message={participant.sensitiveFieldsNotice} />
      )}
    </DetailPage>
  );
};

export default ParticipantDetails;
